import { registerPlugin } from '@capacitor/core';
import { api, apiBase, authHeaders } from './api';
import { isNative, platform } from './platform';

/**
 * Updates for the Android app, which isn't in the Play Store: the server keeps the latest
 * signed APK and a manifest. The app checks on start and when it comes back to the front,
 * and a small Update button downloads and installs it.
 */

export interface Latest {
  versionCode: number;
  versionName: string;
  sha256: string;
  bytes: number;
  /** What changed since the last build, newest first. */
  notes?: string[];
  /** Notes for recent builds, so a phone several versions behind sees all of it. */
  changes?: { versionCode: number; notes: string[] }[];
}

interface ApkUpdatePlugin {
  canInstall(): Promise<{ allowed: boolean }>;
  openInstallSettings(): Promise<void>;
  install(opts: { url: string; token: string | null; sha256: string }): Promise<void>;
  addListener(
    event: 'progress',
    fn: (p: { done: number; total: number }) => void,
  ): Promise<{ remove: () => Promise<void> }>;
}

const ApkUpdate = registerPlugin<ApkUpdatePlugin>('ApkUpdate');

/** The newer build on the server, or null when this one is current (or this isn't Android). */
export async function checkForUpdate(): Promise<Latest | null> {
  if (!isNative || platform !== 'android') return null;
  try {
    const { App } = await import('@capacitor/app');
    const [latest, info] = await Promise.all([
      api<Latest>('/api/app/android/latest.json'),
      App.getInfo(),
    ]);
    const installed = Number(info.build);
    if (latest.versionCode <= installed) return null;
    // Straight to the newest build, however many were skipped; list everything they changed.
    const changes = Array.isArray(latest.changes) ? latest.changes : [];
    const missed = changes.filter((c) => c.versionCode > installed).flatMap((c) => c.notes);
    const notes = missed.length ? missed : (latest.notes ?? []);
    return { ...latest, notes: [...new Set(notes)] };
  } catch {
    return null;
  }
}

export type InstallResult = 'installing' | 'needs-permission';

/** Downloads and opens Android's installer; asks once for permission to install updates. */
export async function installUpdate(
  latest: Latest,
  onProgress: (share: number) => void,
): Promise<InstallResult> {
  const { allowed } = await ApkUpdate.canInstall();
  if (!allowed) {
    await ApkUpdate.openInstallSettings();
    return 'needs-permission';
  }
  const sub = await ApkUpdate.addListener('progress', ({ done, total }) =>
    onProgress(total > 0 ? done / total : done / latest.bytes),
  );
  try {
    const token = authHeaders().authorization?.replace(/^Bearer /, '') ?? null;
    await ApkUpdate.install({ url: `${apiBase()}/api/app/android`, token, sha256: latest.sha256 });
    return 'installing';
  } finally {
    await sub.remove();
  }
}
